import {
  OPT_IN_PREFERENCE,
  OptInPreference,
} from '@/constants/features/opt-in';
import {
  CONSENT_CHECKBOXES,
  ConsentCheckboxId,
  StoredDisclosureFlags,
} from '../config/checkboxes';
import { IConsentVariant } from '../config/variants';
import { grantedBuckets, normalizePreferences } from './bucket-grants';

// Adds this submit's grants to what the user already holds. The consent screen
// only ever grants, never revokes: the one path that takes a bucket away is the
// Settings toggles, via setTogglePreferences in useFlagsConfig.hook.ts.
export const addGrantsToStoredBuckets = (
  existing: OptInPreference[],
  visible: ConsentCheckboxId[],
  checked: ConsentCheckboxId[]
): OptInPreference[] => {
  // Only checkboxes on screen may grant, so a stray checked id can't write a
  // hidden bucket.
  const granted = grantedBuckets(
    checked.filter(checkboxId => visible.includes(checkboxId)),
    visible
  );
  return normalizePreferences([...existing, ...granted]);
};

// Returns only the disclosures ticked on this visit, never a false. A stored
// acceptance can go from unset to true and no further, so nothing here can
// un-accept one; the server enforces the same rule on these flags.
export const newlyAcceptedDisclosureFlags = (
  variant: IConsentVariant,
  checked: ConsentCheckboxId[],
  flags: StoredDisclosureFlags
): StoredDisclosureFlags => {
  const updates: StoredDisclosureFlags = {};
  variant.checkboxes.forEach(checkboxId => {
    const flagKey = CONSENT_CHECKBOXES[checkboxId].disclosureFlagKey;
    if (
      flagKey !== undefined &&
      checked.includes(checkboxId) &&
      flags[flagKey] !== true
    ) {
      updates[flagKey] = true;
    }
  });
  return updates;
};

// Safety net for a GrowthBook targeting mistake: a screen with no clickstream
// checkbox shown to a user who doesn't already hold clickstream consent. Every
// other bucket depends on it, so holding buckets without it is an invalid state.
// Judged on the resulting bucket list rather than on the checkboxes, because the
// US screens expect clickstream from an existing grant, not from a tick.
export const isMissingClickstreamConsent = (next: OptInPreference[]): boolean =>
  next.length > 0 && !next.includes(OPT_IN_PREFERENCE.CLICK_STREAM);
